import { useEffect, useCallback, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook for subscribing to real-time SSE updates
 * @param {Object} handlers - Object with event type as key and handler function as value
 * @returns {Object} - { isConnected, reconnect }
 */
export const useRealtimeUpdates = (handlers = {}) => {
    const eventSourceRef = useRef(null);
    const handlersRef = useRef(handlers);
    const reconnectTimeoutRef = useRef(null);
    const isConnectedRef = useRef(false);

    // Update handlers ref when handlers change
    useEffect(() => {
        handlersRef.current = handlers;
    }, [handlers]);

    const connect = useCallback(() => {
        // Close existing connection if any
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }

        // Clear any pending reconnect
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }

        try {
            const eventSource = new EventSource(`${BACKEND_URL}/api/events`);
            eventSourceRef.current = eventSource;

            eventSource.onopen = () => {
                isConnectedRef.current = true;
            };

            eventSource.onerror = (error) => {
                isConnectedRef.current = false;
                eventSource.close();
                
                // Attempt reconnect after 5 seconds
                reconnectTimeoutRef.current = setTimeout(() => {
                    connect();
                }, 5000);
            };

            // Handle 'connected' event
            eventSource.addEventListener('connected', (event) => {
            });

            // Handle blocked date events
            eventSource.addEventListener('blocked_date_added', (event) => {
                const data = JSON.parse(event.data);
                if (handlersRef.current.onBlockedDateAdded) {
                    handlersRef.current.onBlockedDateAdded(data);
                }
            });

            eventSource.addEventListener('blocked_date_removed', (event) => {
                const data = JSON.parse(event.data);
                if (handlersRef.current.onBlockedDateRemoved) {
                    handlersRef.current.onBlockedDateRemoved(data);
                }
            });

            // Handle slot events
            eventSource.addEventListener('slot_added', (event) => {
                const data = JSON.parse(event.data);
                if (handlersRef.current.onSlotAdded) {
                    handlersRef.current.onSlotAdded(data);
                }
            });

            eventSource.addEventListener('slot_removed', (event) => {
                const data = JSON.parse(event.data);
                if (handlersRef.current.onSlotRemoved) {
                    handlersRef.current.onSlotRemoved(data);
                }
            });

            eventSource.addEventListener('slots_bulk_added', (event) => {
                const data = JSON.parse(event.data);
                if (handlersRef.current.onSlotsBulkAdded) {
                    handlersRef.current.onSlotsBulkAdded(data);
                }
            });

            eventSource.addEventListener('slot_availability_changed', (event) => {
                const data = JSON.parse(event.data);
                if (handlersRef.current.onSlotAvailabilityChanged) {
                    handlersRef.current.onSlotAvailabilityChanged(data);
                }
            });

            // Handle hold expired events
            eventSource.addEventListener('hold_expired', (event) => {
                const data = JSON.parse(event.data);
                if (handlersRef.current.onHoldExpired) {
                    handlersRef.current.onHoldExpired(data);
                }
            });

        } catch (error) {
        }
    }, []);

    const disconnect = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
        isConnectedRef.current = false;
    }, []);

    useEffect(() => {
        connect();

        return () => {
            disconnect();
        };
    }, [connect, disconnect]);

    return {
        isConnected: isConnectedRef.current,
        reconnect: connect
    };
};

export default useRealtimeUpdates;
